import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { AlertHeader, Button, Checkbox, H3, LoaderSpin, ModalCloseButton, ModalContent, ModalFactory, ModalFooter, ModalText, Text } from 'components';
import { theme, toRem } from 'design';
import routeConstants from 'routes/routeConstants';
import { getCalculatedTipsRequest, getPOSDataRequest, setCalculatorPayees, updateCalculatorPaymentset } from 'store/TipCalculator';
import { ICalculatorPaymentSet, ICalculatorRules, IDaySegment, IHoldbackFixed, IHoldbackPercent, IPOSResults, ITipCalculatorRequest, ITipCalculatorResult } from 'types';
import { calculateTimeDifference, getDateTimeWithoutZone, toTitleCase } from 'utils';
import { centsToDollarsDisplay } from 'utils/currency';
import {
  buildTipCalculatorPayeeFromPOSData,
  buildTipCalculatorRequests,
  calculateCalculatorPaymentSetTotals,
  getSharedPOSJobTitles,
  PosUnclosedChecksError,
  TipCalculationNoDataError,
  TipCalculationNoLaborForTicketsError,
  TipCalculationValidationError,
  TipCalculationValidationType,
  validateTipRulesAgainstLaborData,
} from 'utils/tipCalculator';

interface IIntegrationDataProcessingModalProps {
  siteId: string;
  siteHoldbackSettings: IHoldbackFixed | IHoldbackPercent;
  businessDate: string;
  tipCalcRules: ICalculatorRules;
  segments: IDaySegment[];
  paymentSet: ICalculatorPaymentSet;
  creditCardPercentFee: number;
  timeZone: string;
  onClose(): void;
}

interface IContentProps {
  businessDate: string;
  message: string;
  stepMessage: string;
  status: 'loading' | 'error' | 'validationError' | 'success';
  buttonText: string;
  errors?: any;
  timeZone?: string;
  onClick?(skipNoLabor?: boolean): void;
  onClose(): void;
}

const Layout = styled.section`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(540)};
  height: auto;
  margin: ${toRem(theme.layout.gutter.large)} 0;
  padding: ${toRem(theme.modal.spacing.large)};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  h3.modal-header-text {
    font-size: ${toRem(20)};
  }

  h3.modal-header-text-error {
    color: ${theme.color.danger.medium};
    margin-top: ${toRem(22)};
  }

  .modal-text {
    margin-top: ${toRem(12)};
    margin-bottom: ${toRem(28)};
  }

  .modal-step-message {
    margin-top: ${toRem(28)};
    text-align: center;
  }

  header {
    padding-bottom: 0;

    div {
      margin-bottom: 0;
    }
  }

  footer {
    padding-top: ${toRem(22)};

    button {
      width: 100%;
    }
  }
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding: ${toRem(22)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  margin-top: ${toRem(12)};
  text-align: left;

  h3 {
    margin-bottom: ${toRem(8)};
  }

  &.validation-error {
    h3 {
      margin-bottom: ${toRem(8)};
      text-transform: capitalize;
    }

    p {
      font-size: ${toRem(14)};
    }

    &.alert {
      background-color: ${theme.color.warning.light};
      border: ${toRem(2)} solid ${theme.color.warning.medium};
    }
  }
`;

const EmployeeWrapper = styled.div<{ noBorder: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  align-self: stretch;
  margin: ${toRem(8)} 0 0 0;
  padding: ${toRem(8)} 0 ${toRem(12)} 0;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  ${({ noBorder }) =>
    noBorder &&
    css`
      border-bottom-width: 0;
      margin-bottom: 0;
      padding-bottom: 0;
    `}
`;

const EmployeeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const EmployeeItem = styled(EmployeeHeader)`
  p {
    font-size: ${toRem(12)} !important;
    color: ${theme.color.neutral[500]};
  }
`;

const JobList = styled.ul`
  list-style: none;
  padding: ${toRem(12)} 0;
`;

const JobListItem = styled.li`
  text-transform: capitalize;
  font-size: ${toRem(14)};
  margin: ${toRem(4)} 0;
`;

const TicketList = styled.div`
  padding: ${toRem(12)} 0;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
`;

const TicketListItemWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
`;

const TicketListItem = styled.div<{ alignRight?: boolean }>`
  text-transform: capitalize;
  font-size: ${toRem(14)};
  margin: ${toRem(4)} 0;
  width: 25%;
  word-break: break-word;
  text-align: left;
  justify-content: ${(props) => (props.alignRight ? 'flex-end' : undefined)};
`;

const EmployeeUnknown = styled.span`
  color: ${theme.color.neutral[400]};
  font-style: italic;
`;

const CheckBoxWrapper = styled.div`
  margin-top: ${toRem(16)};
  width: 100%;
`;

const validationTitle = (type: TipCalculationValidationType) => {
  if (type === TipCalculationValidationType.NO_RULE_FOR_JOB) return 'Non-tipped Jobs';
  if (type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB) return 'Tips Found for Jobs without Rules';
  if (type === TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB) return 'These Tips Will Be Ignored';
  if (type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES) return 'Invalid Time Clock Entries';
  if (type === TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN) return 'Employees Still Clocked In';
  if (type === TipCalculationValidationType.EMPLOYEES_NO_BREAK_END) return 'Employees Without Break End';
  return type.replaceAll('_', ' ');
};

const Content: React.FC<IContentProps> = ({ businessDate, buttonText, message, stepMessage, status, errors, onClick, onClose, timeZone }) => {
  const [skipNoLabor, setSkipNoLabor] = useState<boolean>(undefined);

  const handleClick = () => {
    onClick(skipNoLabor);
  };

  return (
    <Layout data-cy="integration-data-procesing-modal">
      <ModalCloseButton onClick={onClose} />
      <ModalContent>
        <H3 className="modal-header-text">{dayjs(businessDate).format('MMMM Do, YYYY')}</H3>
        <ModalText className="modal-text">{message}</ModalText>
        {status === 'loading' && <LoaderSpin size={60} stroke={5} color={theme.color.secondary[400]} />}
        {status === 'error' && <AlertHeader />}
        {status === 'success' && <AlertHeader icon="Success" iconFill={theme.color.secondary[400]} />}
        {stepMessage && <H3 className="modal-step-message">{stepMessage}</H3>}

        {/* Display NO_LABOR_FOR_TICKETS */}
        {errors instanceof TipCalculationNoLaborForTicketsError &&
          errors.errors.map((error, i) => (
            <React.Fragment key={`error_${i}`}>
              <ErrorDetails data-cy="tip-calc-validation-errors" className="validation-error">
                <H3 fontWeight="bold">No Labor For Tickets</H3>
                <Text>The tickets below are assigned to employees that did not clock in. To proceed, ensure that these checks are assigned to an employee that clocked in on this business day and try again.</Text>
                <TicketList data-cy="no-labor-ticket-list">
                  <TicketListItemWrapper data-cy="no-labor-ticket-list-header">
                    <TicketListItem>
                      <b>Ticket</b>
                    </TicketListItem>
                    <TicketListItem>
                      <b>Owner</b>
                    </TicketListItem>
                    <TicketListItem>
                      <b>Tip</b>
                    </TicketListItem>
                    <TicketListItem>
                      <b>Auto Gratuity</b>
                    </TicketListItem>
                  </TicketListItemWrapper>
                  {error.tickets?.map((ticket) => (
                    <TicketListItemWrapper key={ticket.displayId} data-cy={`ticket-${ticket.ticketId}`}>
                      <TicketListItem>{ticket.displayId}</TicketListItem>
                      <TicketListItem>{ticket?.employee ? `${ticket?.employee.firstName} ${ticket?.employee.lastName}` : <EmployeeUnknown>Unknown</EmployeeUnknown>}</TicketListItem>
                      <TicketListItem>{centsToDollarsDisplay(ticket.totals.tip.net)}</TicketListItem>
                      <TicketListItem>{centsToDollarsDisplay(ticket.totals.autoGratuity.net)}</TicketListItem>
                    </TicketListItemWrapper>
                  ))}
                </TicketList>
              </ErrorDetails>
              {error.allowSkip === true && (
                <CheckBoxWrapper data-cy="checkbox-wrapper">
                  <Checkbox
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSkipNoLabor(e.target.checked);
                    }}
                    id="skipNoLabor"
                    name="skipNoLabor"
                    data-cy="skip-no-labor"
                    size="small"
                    checked={skipNoLabor}
                    label="Ignore no labor tickets"
                  />
                </CheckBoxWrapper>
              )}
            </React.Fragment>
          ))}

        {/* Display unclosed checks */}
        {errors instanceof PosUnclosedChecksError &&
          errors.errors.map((error, i) => (
            <React.Fragment key={`error_${i}`}>
              <ErrorDetails data-cy="tip-calc-validation-errors" className="validation-error">
                <H3 fontWeight="bold">You have unclosed checks.</H3>
                <Text>Please apply the final tip amount and close any open checks, then try again.</Text>
                <TicketList data-cy="unclosed-checks-list">
                  <TicketListItemWrapper data-cy="unclosed-checks-list-header">
                    <TicketListItem>
                      <b>Ticket</b>
                    </TicketListItem>
                    <TicketListItem>
                      <b>Owner</b>
                    </TicketListItem>
                    <TicketListItem alignRight>
                      <b>Amount</b>
                    </TicketListItem>
                  </TicketListItemWrapper>
                  {error.tickets?.map((ticket) => (
                    <TicketListItemWrapper key={ticket.ticketId} data-cy={`ticket-${ticket.ticketId}`}>
                      <TicketListItem>{ticket.displayId}</TicketListItem>
                      <TicketListItem>{ticket?.employee ? `${ticket.employee.firstName} ${ticket.employee.lastName}` : <EmployeeUnknown>Unknown</EmployeeUnknown>}</TicketListItem>
                      <TicketListItem alignRight>{centsToDollarsDisplay(ticket.amount)}</TicketListItem>
                    </TicketListItemWrapper>
                  ))}
                </TicketList>
              </ErrorDetails>
            </React.Fragment>
          ))}

        {/* Display EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES */}
        {errors instanceof TipCalculationValidationError &&
          errors.errors
            .filter((e) => e.type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES)
            .map((error) => (
              <ErrorDetails key={`error_${error.type}`} data-cy="tip-calc-validation-errors" className="validation-error">
                <H3 fontWeight="bold">{validationTitle(error.type)}</H3>
                <Text>{error.message}</Text>
                {error.employees.map((employee, idx) => (
                  <EmployeeWrapper data-cy="employee-invalid-time-clock-entry" key={`employee_${idx}`} noBorder={error.employees.length - 1 === idx}>
                    <EmployeeHeader>
                      <Text fontWeight="bold">{employee.name}</Text>
                      <Text fontWeight="bold">{toTitleCase(employee.jobTitle)}</Text>
                    </EmployeeHeader>
                    <EmployeeItem>
                      <Text>Clock In</Text>
                      <Text>{getDateTimeWithoutZone(employee.start, timeZone)}</Text>
                    </EmployeeItem>
                    <EmployeeItem>
                      <Text>Clock Out</Text>
                      <Text>{getDateTimeWithoutZone(employee.end, timeZone)}</Text>
                    </EmployeeItem>
                    <EmployeeItem>
                      <Text>Time Worked</Text>
                      <Text>{calculateTimeDifference(employee.start, employee.end)}</Text>
                    </EmployeeItem>
                  </EmployeeWrapper>
                ))}
              </ErrorDetails>
            ))}

        {/* Display NO_TIP_SHARE_RULE_FOR_JOB */}
        {errors instanceof TipCalculationValidationError &&
          errors.errors
            .filter((e) => e.type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB)
            .map((error) => (
              <ErrorDetails key={`error_${error.type}`} data-cy="tip-calc-validation-errors" className="validation-error">
                <H3 fontWeight="bold">{validationTitle(error.type)}</H3>
                <Text>{error.message}</Text>
                <JobList>
                  {error.jobs?.map(
                    (job, i) =>
                      typeof job !== 'string' &&
                      job.employees
                        .filter((emp) => emp.total > 0)
                        .map((employee, j) => (
                          <JobListItem key={`$job_${i}_employee_${j}`}>
                            <b>{employee.name}</b> - {job.title} - {centsToDollarsDisplay(employee.total)}
                          </JobListItem>
                        ))
                  )}
                </JobList>
              </ErrorDetails>
            ))}

        {/* Display ZERO_POINT_RULE_FOR_JOB */}
        {errors instanceof TipCalculationValidationError &&
          errors.errors
            .filter((e) => e.type === TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB)
            .map((error) => (
              <ErrorDetails key={`error_${error.type}`} data-cy="tip-calc-validation-errors" className="validation-error alert">
                <H3 fontWeight="bold">{validationTitle(error.type)}</H3>
                <Text>{error.message}</Text>
                <JobList>
                  {error.jobs?.map(
                    (job, i) =>
                      typeof job !== 'string' &&
                      job.employees
                        .filter((emp) => emp.total > 0)
                        .map((employee, j) => (
                          <JobListItem key={`$job_${i}_employee_${j}`}>
                            <b>{employee.name}</b> - {job.title} - {centsToDollarsDisplay(employee.total)}
                          </JobListItem>
                        ))
                  )}
                </JobList>
                <Text fontWeight="bold">For assistance, contact Kickfin Support.</Text>
              </ErrorDetails>
            ))}

        {errors instanceof TipCalculationValidationError &&
          !errors.errors.some((e) => e.type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB || e.type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES) &&
          errors.errors
            .filter((e) => e.type !== TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES && e.type !== TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB)
            .map((error) => (
              <ErrorDetails key={`error_${error.type}`} data-cy="tip-calc-validation-errors" className="validation-error">
                <H3 fontWeight="bold">{validationTitle(error.type)}</H3>
                <Text>{error.message}</Text>
                <JobList>
                  {error.jobs?.map(
                    (job, i) =>
                      typeof job !== 'string' &&
                      job.employees.map((employee, j) => (
                        <JobListItem key={`$job_${i}_employee_${j}`}>
                          <b>{employee.name}</b> - {job.title}
                        </JobListItem>
                      ))
                  )}
                </JobList>
              </ErrorDetails>
            ))}

        {errors && errors instanceof TipCalculationNoDataError && (
          <ErrorDetails data-cy="integration-data-procesing-errors">
            <H3 fontWeight="bold">Details</H3>
            <Text>{errors.message}</Text>
          </ErrorDetails>
        )}

        {errors && !(errors instanceof TipCalculationValidationError) && errors.length && errors[0].msg && !errors[0].tickets && (
          <ErrorDetails data-cy="integration-data-procesing-errors">
            <H3 fontWeight="bold">Details</H3>
            <Text>{errors[0].msg}</Text>
          </ErrorDetails>
        )}
      </ModalContent>
      <ModalFooter columns={1}>
        <Button data-cy="integration-data-action-btn" label={buttonText} type="button" appearance="primary" size="large" onClick={handleClick} isProcessing={false} disabled={status === 'loading'} />
      </ModalFooter>
    </Layout>
  );
};

export const IntegrationDataProcessingModal: React.FC<IIntegrationDataProcessingModalProps> = ({ businessDate, siteId, siteHoldbackSettings, creditCardPercentFee, tipCalcRules, segments, paymentSet, onClose, timeZone }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  let currentPOSResults: IPOSResults;

  const ContentProps: Record<string, Partial<IContentProps>> = {
    loadPOSData: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Retrieving Data...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    loadPOSError: {
      message: 'Something went wrong',
      stepMessage: 'We are having trouble retrieving your data.',
      status: 'error',
      buttonText: 'Reload Data',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationError: {
      message: 'Please review the warnings below before proceeding.',
      status: 'validationError',
      buttonText: 'Calculate Payments',
      onClick: () => onCalculateTips(),
    },
    tipCalcRuleValidationInvalidTimeEntriesError: {
      message: 'The issue below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationNoClockOutError: {
      message: 'The warning below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationNoBreakEndError: {
      message: 'The warning below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationNoLaborForTicketsError: {
      message: 'The issue below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: (skipNoLabor?: boolean) => loadPOSData(skipNoLabor),
    },
    unclosedChecksError: {
      message: 'The issue below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationTippedJobsWithoutRulesError: {
      message: 'The issues below must be resolved before proceeding.',
      status: 'validationError',
      buttonText: 'Retry',
      onClick: () => loadPOSData(),
    },
    tipCalcRuleValidationNoEligibleEmployeesError: {
      message: 'Based on your rules, there are no employees who worked matching your rules. Please check your configuration.',
      status: 'validationError',
      buttonText: 'Go to Settings',
      onClick: () => history.push(`${routeConstants.private.settingsCalculator}/tip-shares`),
    },
    calculateTips: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Calculating tips...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    calculateSuccess: {
      message: 'All systems go!',
      stepMessage: 'Success!',
      status: 'success',
      buttonText: 'Review Payouts',
      onClick: () => {
        history.push(routeConstants.private.makePaymentCalculatorReview);
        onClose();
      },
    },
    calculateError: {
      stepMessage: 'An error has occurred calculating tips',
      status: 'error',
      buttonText: 'Try Again',
      onClick: () => loadPOSData(),
    },
  };

  const [contentProps, setContentProps] = useState<Partial<IContentProps>>(ContentProps.loadPOSData);

  const onPOSDataSuccess = (data: IPOSResults) => {
    try {
      // set results
      currentPOSResults = data;
      const tipCalcRequests = buildTipCalculatorRequests(currentPOSResults, tipCalcRules, segments);

      // validate labor data against rules
      validateTipRulesAgainstLaborData(currentPOSResults, tipCalcRules, tipCalcRequests);

      onCalculateTips();
    } catch (error: any) {
      if (error instanceof TipCalculationNoDataError) {
        onPOSDataFailure(error); // no data found
      } else if (error instanceof TipCalculationValidationError) {
        onTipRuleValidationError(error); // show tip rule validation error.
      } else {
        onPOSDataFailure(error);
      }
    }
  };

  const onPOSDataFailure = (errors: any) => {
    if (isMounted.current) {
      if (errors.length && errors.some((error: any) => error.code === 'pos_unclosed_checks')) {
        setContentProps({ ...ContentProps.unclosedChecksError, errors: new PosUnclosedChecksError(errors) });
      } else if (errors.length && errors.some((error: any) => error.code === 'pos_no_labor_for_ticket')) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationNoLaborForTicketsError, errors: new TipCalculationNoLaborForTicketsError('No labor for tickets', errors) });
      } else {
        setContentProps({ ...ContentProps.loadPOSError, errors });
      }
    }
  };

  const onTipRuleValidationError = (validationError: TipCalculationValidationError) => {
    if (isMounted.current) {
      if (validationError.errors.find((e) => e.type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES)) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationInvalidTimeEntriesError, errors: validationError });
      } else if (validationError.errors.find((e) => e.type === TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN)) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationNoClockOutError, errors: validationError });
      } else if (validationError.errors.find((e) => e.type === TipCalculationValidationType.EMPLOYEES_NO_BREAK_END)) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationNoBreakEndError, errors: validationError });
      } else if (validationError.errors.find((e) => e.type === TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES)) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationNoEligibleEmployeesError, errors: validationError });
      } else if (validationError.errors.find((e) => e.type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB)) {
        setContentProps({ ...ContentProps.tipCalcRuleValidationTippedJobsWithoutRulesError, errors: validationError });
      } else {
        setContentProps({ ...ContentProps.tipCalcRuleValidationError, errors: validationError });
      }
    }
  };

  const loadPOSData = (skipNoLabor?: boolean) => {
    dispatch(getPOSDataRequest(siteId, dayjs(businessDate).format('YYYY-MM-DD'), onPOSDataSuccess, onPOSDataFailure, skipNoLabor));

    if (isMounted.current) setContentProps(ContentProps.loadPOSData);
  };

  const onTipCalcSuccess = (tipCalcResults: ITipCalculatorResult[]) => {
    try {
      // calculate payment set totals based on pos data
      const paymentSetWithTotals = calculateCalculatorPaymentSetTotals(paymentSet, currentPOSResults);
      dispatch(updateCalculatorPaymentset(paymentSetWithTotals));

      const payees = buildTipCalculatorPayeeFromPOSData(tipCalcResults, currentPOSResults, siteHoldbackSettings, getSharedPOSJobTitles(tipCalcRules));

      dispatch(setCalculatorPayees(payees));

      if (isMounted.current) setContentProps(ContentProps.calculateSuccess);
    } catch (err) {
      // TODO: sync with team to figure out how to handle errors here
      //   - should we manually log this error but show a generic error message to the user?

      // eslint-disable-next-line no-console
      console.log('error building tip calculator payee from labor ticket data', err);

      if (isMounted.current) setContentProps({ ...ContentProps.calculateError, errors: [err] });
    }
  };

  const onTipCalcFailure = (errors: any) => {
    if (isMounted.current) setContentProps({ ...ContentProps.calculateError, errors });
  };

  const onCalculateTips = () => {
    try {
      const tipCalcRequests = buildTipCalculatorRequests(currentPOSResults, tipCalcRules, segments);

      calculateTips(
        tipCalcRequests.filter((req) => req.employees.length > 0),
        currentPOSResults
      );
    } catch (error: any) {
      onPOSDataFailure(error);
    }
  };

  const calculateTips = (tipCalcRequests: ITipCalculatorRequest[], currentPOSResults: IPOSResults) => {
    dispatch(getCalculatedTipsRequest(tipCalcRequests, currentPOSResults, creditCardPercentFee, onTipCalcSuccess, onTipCalcFailure));
    if (isMounted.current) setContentProps(ContentProps.calculateTips);
  };

  // initialize by loading data & mounting the component
  useEffect(() => {
    loadPOSData();
    isMounted.current = true;
  }, [siteId]);

  return (
    <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false}>
      <Content
        businessDate={businessDate}
        buttonText={contentProps.buttonText}
        errors={contentProps.errors}
        message={contentProps.message}
        onClick={contentProps.onClick}
        onClose={onClose}
        stepMessage={contentProps.stepMessage}
        status={contentProps.status}
        timeZone={timeZone}
      />
    </ModalFactory>
  );
};
